import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import bearMarket from 'assets/4yrBear.svg';
import accumulationYear from 'assets/4yrAccu.svg';
import btcHalving from 'assets/4yrBtc.svg';
import bullMarket from 'assets/4yr_Bull.svg';
import './four-year-cycle.css';
import { Box, Card, Typography, IconButton, useMediaQuery, useTheme } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

const CYCLES = [
  {
    startYear: 2022,
    endYear: 2025,
    halvingDate: new Date(2024, 3, 19, 2, 1),
    cards: [
      { text: 'BEAR MARKET', img: bearMarket, year: '2022' },
      { text: 'ACCUMULATION', img: accumulationYear, year: '2023' },
      { text: 'BTC HALVING', img: btcHalving, year: '2024' },
      { text: 'BULL MARKET', img: bullMarket, year: '2025' },
    ],
  },
  {
    startYear: 2026,
    endYear: 2029,
    halvingDate: new Date(2028, 3, 19, 2, 1),
    cards: [
      { text: 'BEAR MARKET', img: bearMarket, year: '2026' },
      { text: 'ACCUMULATION', img: accumulationYear, year: '2027' },
      { text: 'BTC HALVING', img: btcHalving, year: '2028' },
      { text: 'BULL MARKET', img: bullMarket, year: '2029' },
    ],
  },
];

const SLIDE_MS = 500;
const SLIDE_COUNT = CYCLES.length;
const SLIDE_WIDTH = 100 / SLIDE_COUNT;
const MOBILE_CARD_PAGES = 2;
const LAST_CYCLE_INDEX = SLIDE_COUNT - 1;
const LAST_CARD_PAGE = MOBILE_CARD_PAGES - 1;

const getDefaultCycleIndex = () => (new Date().getFullYear() >= 2026 ? 1 : 0);

const getPositionInRange = (date, startYear, endYear) => {
  const rangeStart = new Date(startYear, 0, 1);
  const rangeEnd = new Date(endYear, 11, 31, 23, 59, 59);
  const total = rangeEnd - rangeStart;
  if (total <= 0) return 0;
  const clamped = Math.min(Math.max(date - rangeStart, 0), total);
  return (clamped / total) * 100;
};

const getCurrentCardIndex = (cycle, date) => {
  const yearStr = String(date.getFullYear());
  const idx = cycle.cards.findIndex((card) => card.year === yearStr);
  return idx >= 0 ? idx : 0;
};

const getCardPageForDate = (cycleIndex, date) => {
  const cycle = CYCLES[cycleIndex];
  if (!cycle) return 0;
  const cardIndex = getCurrentCardIndex(cycle, date);
  return cardIndex <= 1 ? 0 : 1;
};

const renderLabel = (text) => {
  if (text.includes(' ')) {
    return text.split(' ').map((word, index, words) => (
      <React.Fragment key={`${word}-${index}`}>
        {word}
        {index < words.length - 1 ? <br /> : null}
      </React.Fragment>
    ));
  }
  return text;
};

const SingleBox = ({ text, img, year, isCurrentYear }) => (
  <Card
    elevation={0}
    className={`four-year-cycle-card${isCurrentYear ? ' four-year-cycle-card--current' : ''}`}
    sx={{
      bgcolor: 'primary.light',
      borderRight: '0.1px solid #fff',
      padding: { xs: 1, md: 2 },
      py: { xs: 1.5, md: 2 },
      alignItems: 'center',
      justifyContent: { xs: 'flex-start', md: 'center' },
      display: 'flex',
      flexDirection: { xs: 'column', md: 'row' },
      position: 'relative',
      minHeight: { xs: 120, md: 100 },
      overflow: 'hidden',
    }}
  >
    <Typography
      sx={{
        position: 'absolute',
        top: 4,
        right: { xs: 8, md: 15 },
        color: isCurrentYear ? '#95F205' : '#fff',
        zIndex: 2,
        fontSize: { xs: 14, md: 25, lg: 30 },
        lineHeight: 1,
        fontWeight: isCurrentYear ? 800 : 400,
      }}
    >
      {year}
    </Typography>
    <Box
      component="img"
      sx={{
        position: { md: 'static' },
        zIndex: 1,
        height: { xs: 34, md: 'auto' },
        width: { xs: 34, md: 'auto' },
        maxWidth: { xs: 34, md: '100%' },
        objectFit: 'contain',
        opacity: { xs: 0.9, md: 1 },
        mt: { xs: 2.5, md: 0 },
        mb: { xs: 0.5, md: 0 },
        mx: { xs: 'auto', md: 0 },
        flexShrink: 0,
      }}
      src={img}
      alt={text}
    />
    <Typography
      zIndex={1}
      sx={{
        width: '100%',
        fontSize: { xs: 11, sm: 12, md: 15, lg: 24 },
        m: { xs: 0, md: 5 },
        px: { xs: 0.5, md: 0 },
        color: '#fff',
        fontWeight: 'bold',
        textAlign: 'center',
        lineHeight: 1.2,
        whiteSpace: { xs: 'normal', md: 'nowrap' },
      }}
    >
      {renderLabel(text)}
    </Typography>
  </Card>
);

const CycleSlide = ({
  cycle,
  date,
  pulse,
  slideWidth,
  isMobile,
  isActive,
  cardPage,
  onCardPageChange,
}) => {
  const scrollRef = useRef(null);
  const isProgrammaticScroll = useRef(false);
  const currentYear = String(date.getFullYear());

  const needlePosition = useMemo(
    () => getPositionInRange(date, cycle.startYear, cycle.endYear),
    [date, cycle.startYear, cycle.endYear]
  );

  const specialNeedlePosition = useMemo(
    () => getPositionInRange(cycle.halvingDate, cycle.startYear, cycle.endYear),
    [cycle.halvingDate, cycle.startYear, cycle.endYear]
  );

  const showTodayNeedle =
    date.getFullYear() >= cycle.startYear && date.getFullYear() <= cycle.endYear;

  const scrollToPage = useCallback((page) => {
    const el = scrollRef.current;
    if (!isMobile || !el) return;
    isProgrammaticScroll.current = true;
    el.scrollTo({ left: page * el.clientWidth, behavior: 'smooth' });
    window.setTimeout(() => {
      isProgrammaticScroll.current = false;
    }, SLIDE_MS);
  }, [isMobile]);

  useEffect(() => {
    if (!isMobile || !isActive) return;
    scrollToPage(cardPage);
  }, [isMobile, isActive, cardPage, scrollToPage]);

  const handleScroll = (event) => {
    if (!isMobile || !isActive || isProgrammaticScroll.current) return;
    const el = event.currentTarget;
    const nextPage = Math.round(el.scrollLeft / el.clientWidth);
    if (nextPage !== cardPage) {
      onCardPageChange(nextPage);
    }
  };

  const cardsTrack = (
    <Box className="four-year-cycle-cards-track">
      {cycle.cards.map((data) => (
        <SingleBox
          key={data.year}
          {...data}
          isCurrentYear={data.year === currentYear}
        />
      ))}

      {showTodayNeedle && (
        <>
          <div className="needle-4year" style={{ left: `${needlePosition}%` }} />
          <div
            className="date-label"
            style={{ left: `calc(${needlePosition}% - 50px)` }}
          >
            {date.toLocaleDateString('en-US', { day: '2-digit', month: 'short' })}
          </div>
        </>
      )}

      <div
        className="red-needle"
        style={{
          left: `${specialNeedlePosition}%`,
          animation: pulse ? 'pulse 1s' : 'none',
        }}
      />
      <div
        className="date-label"
        style={{
          top: 8,
          left: `calc(${specialNeedlePosition}% - 50px)`,
          color: 'red',
        }}
      >
        {cycle.halvingDate.toLocaleDateString('en-US', {
          day: '2-digit',
          month: 'short',
        })}
      </div>
    </Box>
  );

  return (
    <Box
      className="four-year-cycle-slide"
      sx={{ flex: `0 0 ${slideWidth}%`, width: `${slideWidth}%` }}
    >
      <Box
        className="four-year-cycle-cards-scroll"
        ref={scrollRef}
        onScroll={handleScroll}
      >
        {cardsTrack}
      </Box>
    </Box>
  );
};

function FourYearCycle() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [date, setDate] = useState(new Date());
  const [pulse, setPulse] = useState(false);
  const [activeCycle, setActiveCycle] = useState(getDefaultCycleIndex);
  const [cardPage, setCardPage] = useState(() => getCardPageForDate(getDefaultCycleIndex(), new Date()));
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    const dayInterval = setInterval(() => setDate(new Date()), 86400000);
    const pulseInterval = setInterval(() => setPulse((prev) => !prev), 1000);
    return () => {
      clearInterval(dayInterval);
      clearInterval(pulseInterval);
    };
  }, []);

  useEffect(() => {
    const year = date.getFullYear();
    const cycleIndex = year >= 2026 ? 1 : 0;
    setActiveCycle(cycleIndex);
    setCardPage(getCardPageForDate(cycleIndex, date));
  }, [date]);

  const goToCycle = (index, page = null) => {
    if (index === activeCycle && page === null) return;
    if (isAnimating) return;
    setIsAnimating(true);
    setActiveCycle(index);
    if (page !== null) setCardPage(page);
    window.setTimeout(() => setIsAnimating(false), SLIDE_MS);
  };

  const goPrev = () => {
    if (isAnimating) return;

    if (isMobile) {
      if (cardPage > 0) {
        setCardPage((prev) => prev - 1);
        return;
      }
      if (activeCycle > 0) {
        goToCycle(activeCycle - 1, LAST_CARD_PAGE);
      }
      return;
    }

    if (activeCycle > 0) goToCycle(activeCycle - 1);
  };

  const goNext = () => {
    if (isAnimating) return;

    if (isMobile) {
      if (cardPage < LAST_CARD_PAGE) {
        setCardPage((prev) => prev + 1);
        return;
      }
      if (activeCycle < LAST_CYCLE_INDEX) {
        goToCycle(activeCycle + 1, 0);
      }
      return;
    }

    if (activeCycle < LAST_CYCLE_INDEX) goToCycle(activeCycle + 1);
  };

  const canGoPrev = isMobile
    ? !(activeCycle === 0 && cardPage === 0)
    : activeCycle > 0;

  const canGoNext = isMobile
    ? !(activeCycle === LAST_CYCLE_INDEX && cardPage === LAST_CARD_PAGE)
    : activeCycle < LAST_CYCLE_INDEX;

  const chevronButtonSx = {
    color: '#fff',
    bgcolor: 'primary.light',
    borderRadius: 0,
    alignSelf: 'stretch',
    minWidth: { xs: 36, md: 44 },
    flexShrink: 0,
    zIndex: 2,
    '&:hover': { bgcolor: 'primary.main' },
    '&.Mui-disabled': { color: 'rgba(255,255,255,0.25)', bgcolor: 'primary.light' },
  };

  return (
    <Box
      sx={{
        my: 1,
        bgcolor: 'primary.light',
        boxShadow: '0px 0px 2px #fff',
        display: 'flex',
        flexDirection: 'row',
        position: 'relative',
        height: 'max-content',
        minHeight: { xs: 130, md: 100 },
      }}
    >
      <IconButton
        sx={chevronButtonSx}
        onClick={goPrev}
        disabled={!canGoPrev || isAnimating}
        aria-label={isMobile ? 'Show previous years' : 'Show 2022 to 2025 cycle'}
      >
        <ChevronLeftIcon fontSize="large" />
      </IconButton>

      <Box className="four-year-carousel-viewport">
        <Box
          className="four-year-carousel-track"
          style={{
            width: `${SLIDE_COUNT * 100}%`,
            transform: `translateX(-${activeCycle * SLIDE_WIDTH}%)`,
          }}
        >
          {CYCLES.map((cycle, index) => (
            <CycleSlide
              key={cycle.startYear}
              cycle={cycle}
              date={date}
              pulse={pulse}
              slideWidth={SLIDE_WIDTH}
              isMobile={isMobile}
              isActive={index === activeCycle}
              cardPage={cardPage}
              onCardPageChange={setCardPage}
            />
          ))}
        </Box>
      </Box>

      <IconButton
        sx={chevronButtonSx}
        onClick={goNext}
        disabled={!canGoNext || isAnimating}
        aria-label={isMobile ? 'Show next years' : 'Show 2026 to 2029 cycle'}
      >
        <ChevronRightIcon fontSize="large" />
      </IconButton>
    </Box>
  );
}

export default FourYearCycle;
